import Image from 'next/image';
import { memo, useCallback, useMemo } from 'react';

import { Tooltip } from '@air/primitive-tooltip';
import { type SelectableItem } from '@air/redux-selected-items';

import canvasCardPlaceholder from '~/assets/canvas-card-placeholder.png';
import BoardThumbnail from '~/components/BoardThumbnail/BoardThumbnail';
import { ClipThumbnail } from '~/components/Shared/ClipList/ClipItem/ClipThumbail';
import { isSelectableGalleryItem } from '~/store/selectedItems/types';
import { ClipIconSize } from '~/types/clip-icon-size';

const THUMB_SIZE = 32;
const MAX_VISIBLE_THUMBNAILS = 4;

type AgentSelectionBarThumbnailProps = {
  item: SelectableItem;
};

const BoardThumbnailThumb = memo(({ item }: AgentSelectionBarThumbnailProps) => {
  if (!isSelectableGalleryItem(item) || (item.type !== 'board' && item.type !== 'topLevelBoard')) {
    return null;
  }

  return (
    <BoardThumbnail
      board={item.item}
      height={THUMB_SIZE}
      objectFit="cover"
      sizes="32px"
      width={THUMB_SIZE}
      borderRadius={0}
    />
  );
});

BoardThumbnailThumb.displayName = 'BoardThumbnailThumb';

const ClipThumbnailThumb = memo(({ item }: AgentSelectionBarThumbnailProps) => {
  if (!isSelectableGalleryItem(item) || (item.type !== 'asset' && item.type !== 'file')) {
    return null;
  }

  const clip = item.item;

  return (
    <ClipThumbnail
      className="size-full object-cover"
      description={clip.description}
      ext={clip.ext}
      height={THUMB_SIZE}
      hideText
      iconSize={ClipIconSize.small}
      importedName={clip.importedName}
      size="small"
      src={clip.assets.image}
      status={clip.status}
      type={clip.type}
      width={THUMB_SIZE}
      borderRadius={0}
    />
  );
});

ClipThumbnailThumb.displayName = 'ClipThumbnailThumb';

const CanvasThumbnailThumb = memo(({ item }: AgentSelectionBarThumbnailProps) => {
  if (!isSelectableGalleryItem(item) || item.type !== 'canvas') {
    return null;
  }

  return (
    <Image
      alt=""
      className="size-full object-cover object-center"
      height={THUMB_SIZE}
      src={canvasCardPlaceholder.src}
      width={THUMB_SIZE}
    />
  );
});

CanvasThumbnailThumb.displayName = 'CanvasThumbnailThumb';

const getItemTitle = (item: SelectableItem): string => {
  if (!isSelectableGalleryItem(item)) {
    return 'Untitled';
  }

  if (item.type === 'board' || item.type === 'topLevelBoard') {
    return item.item.title || 'Untitled';
  }

  if (item.type === 'asset' || item.type === 'file') {
    return item.item.title || item.item.importedName || 'Untitled';
  }

  if (item.type === 'canvas') {
    return item.item.title || 'Untitled';
  }

  return 'Untitled';
};

export type AgentSelectionBarThumbnailStripProps = {
  items: SelectableItem[];
};

/**
 * Overlapping stack of selected-item thumbnails with a trailing `+N` square, rendered by the agent
 * variant of the selection bar.
 */
export const AgentSelectionBarThumbnailStrip = memo(({ items }: AgentSelectionBarThumbnailStripProps) => {
  const visibleItems = useMemo(() => items.slice(0, MAX_VISIBLE_THUMBNAILS), [items]);
  const overflowCount = items.length - visibleItems.length;

  const renderThumbnail = useCallback((item: SelectableItem) => {
    if (!isSelectableGalleryItem(item)) {
      return null;
    }

    if (item.type === 'board' || item.type === 'topLevelBoard') {
      return <BoardThumbnailThumb item={item} />;
    }

    if (item.type === 'asset' || item.type === 'file') {
      return <ClipThumbnailThumb item={item} />;
    }

    if (item.type === 'canvas') {
      return <CanvasThumbnailThumb item={item} />;
    }

    return null;
  }, []);

  if (visibleItems.length === 0) {
    return null;
  }

  return (
    <div className="flex shrink-0 items-center -space-x-2.5" data-testid="AGENT_SELECTION_BAR_THUMBNAILS">
      {visibleItems.map((item, index) => {
        const title = getItemTitle(item);

        return (
          <Tooltip key={item.id} label={title}>
            <div
              aria-label={title}
              className="relative size-8 shrink-0 overflow-hidden rounded-8 border-2 border-black/80 bg-white/10 shadow-s"
              role="img"
              style={{ zIndex: visibleItems.length - index }}
            >
              {renderThumbnail(item)}
            </div>
          </Tooltip>
        );
      })}

      {overflowCount > 0 && (
        <div
          aria-label={`${overflowCount} more selected`}
          className="relative flex size-8 shrink-0 items-center justify-center overflow-hidden rounded-8 border-2 border-black/80 bg-white/10 font-semibold text-10 text-white shadow-s"
          data-testid="AGENT_SELECTION_BAR_OVERFLOW_CHIP"
          role="img"
          style={{ zIndex: 0 }}
        >
          +{overflowCount}
        </div>
      )}
    </div>
  );
});

AgentSelectionBarThumbnailStrip.displayName = 'AgentSelectionBarThumbnailStrip';
